import React, { useMemo, useState } from "react";
import { streamSSE, downloadExport, logChuck } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import {
  Loader2, Download, Copy, ShieldCheck, FileText, ShieldAlert,
  ClipboardList, HeartPulse, Beaker, BookOpen,
} from "lucide-react";
import {
  CsrTableImport, PsurSignalChart, ProtocolSchemaDiagram, schemaAscii,
} from "@/components/TherapeuticsDraftingHelpers";

// -------- drafter definitions -------------------------------------------------
const DRAFTERS = [
  {
    code: "csr",
    label: "ICH E3 CSR",
    icon: FileText,
    hint: "Clinical Study Report",
    path: "/therapeutics/csr/generate",
    docType: "csr",
    heading: "Clinical Study Report (ICH E3).",
    subtitle: "Full 16-section CSR grounded strictly in sponsor data. Gaps flagged as [SPONSOR TO PROVIDE].",
    initial: {
      study_title: "", protocol_id: "", sponsor: "", active_ingredient: "",
      indication: "", phase: "Phase 2", design: "", population: "",
      primary_endpoint: "", secondary_endpoints: "", treatment_arms: "",
      n_randomized: "", n_completed: "",
      efficacy_results: "", safety_results: "", key_ae: "", conclusions: "",
      version: "1.0",
    },
    fields: [
      { key: "study_title",         label: "Study title *",             type: "text", required: true },
      { key: "protocol_id",         label: "Protocol ID *",             type: "text", required: true },
      { key: "sponsor",             label: "Sponsor",                    type: "text" },
      { key: "active_ingredient",   label: "Active ingredient *",       type: "text", required: true },
      { key: "indication",          label: "Indication *",               type: "text", required: true },
      { key: "phase",               label: "Phase",                       type: "select", options: ["Phase 1", "Phase 2", "Phase 3", "Phase 4"] },
      { key: "design",              label: "Study design *",             type: "textarea", required: true, placeholder: "e.g., randomized, double-blind, placebo-controlled, parallel group" },
      { key: "population",          label: "Study population *",         type: "textarea", required: true, placeholder: "inclusion/exclusion summary" },
      { key: "primary_endpoint",    label: "Primary endpoint *",         type: "textarea", required: true },
      { key: "secondary_endpoints", label: "Secondary endpoints",         type: "textarea" },
      { key: "treatment_arms",      label: "Treatment arms *",           type: "textarea", required: true, placeholder: "e.g., Drug X 50mg QD vs placebo" },
      { key: "n_randomized",        label: "N randomized",                type: "number" },
      { key: "n_completed",         label: "N completed",                 type: "number" },
      { key: "efficacy_results",    label: "Efficacy results (sponsor)", type: "textarea" },
      { key: "safety_results",      label: "Safety results (sponsor)",   type: "textarea" },
      { key: "key_ae",              label: "Key AEs / SAEs",             type: "textarea" },
      { key: "conclusions",         label: "Conclusions",                type: "textarea" },
      { key: "version",             label: "CSR version",                type: "text" },
    ],
  },
  {
    code: "psur",
    label: "PADER / PSUR",
    icon: ShieldAlert,
    hint: "Periodic safety report",
    path: "/therapeutics/periodic-safety/generate",
    docType: "periodic_safety",
    heading: "Periodic Safety Report — PADER or PSUR.",
    subtitle:
      "PADER per 21 CFR 314.80 (US) or PSUR/PBRER per ICH E2C(R2). We pull live FAERS reactions for the active ingredient so every claim is grounded.",
    initial: {
      report_type: "PADER", product_name: "", active_ingredient: "",
      application_number: "", sponsor: "",
      reporting_period_start: "", reporting_period_end: "",
      international_birth_date: "", indications: "",
      marketing_status: "", exposure_estimate: "",
      sponsor_signal_narrative: "", action_taken: "",
    },
    fields: [
      { key: "report_type",              label: "Report type *",         type: "select", options: ["PADER", "PSUR"], required: true },
      { key: "product_name",             label: "Product (brand) *",     type: "text", required: true },
      { key: "active_ingredient",        label: "Active ingredient *",   type: "text", required: true },
      { key: "application_number",       label: "Application # (NDA/BLA)", type: "text" },
      { key: "sponsor",                  label: "Sponsor",                type: "text" },
      { key: "reporting_period_start",   label: "Reporting period start *", type: "date", required: true },
      { key: "reporting_period_end",     label: "Reporting period end *",   type: "date", required: true },
      { key: "international_birth_date", label: "International birth date (PSUR)", type: "date" },
      { key: "indications",              label: "Indications *",           type: "textarea", required: true },
      { key: "marketing_status",         label: "Marketing status (countries)", type: "textarea" },
      { key: "exposure_estimate",        label: "Exposure estimate",       type: "textarea", placeholder: "e.g., 12,400 patient-years; 3.2M units dispensed" },
      { key: "sponsor_signal_narrative", label: "Sponsor signal narrative", type: "textarea" },
      { key: "action_taken",             label: "Actions taken (RSI updates, DHPCs, label changes)", type: "textarea" },
    ],
    metaRender: (meta) => meta?.faers && (
      <div className="border border-slate-200 bg-white p-3">
        <div className="overline mb-1 text-[#2563EB]">FAERS · {meta.faers.total} events · {meta.faers.serious_count} serious</div>
        {(meta.faers.top_reactions || []).slice(0, 6).map((r) => (
          <div key={r.reaction} className="text-[11px] text-slate-700">
            <span className="font-mono">{r.reaction}</span> × {r.count}
          </div>
        ))}
      </div>
    ),
  },
  {
    code: "protocol",
    label: "Protocol",
    icon: ClipboardList,
    hint: "Phase I/II/III (ICH E6 R3)",
    path: "/therapeutics/protocol/generate",
    docType: "protocol",
    heading: "Phase I / II / III Protocol (ICH E6 R3 · SPIRIT-2013).",
    subtitle:
      "Full GCP-compliant clinical trial protocol. Phase-aware guidance (SAD/MAD for Phase 1, adaptive rules for Phase 3).",
    initial: {
      protocol_title: "", protocol_id: "", sponsor: "", active_ingredient: "",
      indication: "", phase: "Phase 1", ind_number: "",
      background: "", rationale: "",
      primary_objective: "", secondary_objectives: "", exploratory_objectives: "",
      design_summary: "", population: "", inclusion_criteria: "", exclusion_criteria: "",
      dose_regimen: "", n_subjects: "", duration_weeks: "",
      stats_summary: "", safety_monitoring: "", version: "1.0",
    },
    fields: [
      { key: "protocol_title",        label: "Protocol title *",         type: "text", required: true },
      { key: "protocol_id",           label: "Protocol ID *",            type: "text", required: true },
      { key: "sponsor",               label: "Sponsor",                    type: "text" },
      { key: "active_ingredient",     label: "Active ingredient *",       type: "text", required: true },
      { key: "indication",            label: "Indication *",              type: "text", required: true },
      { key: "phase",                 label: "Phase *",                    type: "select", options: ["Phase 1", "Phase 2", "Phase 3"], required: true },
      { key: "ind_number",            label: "IND #",                       type: "text" },
      { key: "background",            label: "Background (disease + drug)", type: "textarea" },
      { key: "rationale",             label: "Rationale",                   type: "textarea" },
      { key: "primary_objective",     label: "Primary objective *",         type: "textarea", required: true },
      { key: "secondary_objectives",  label: "Secondary objectives",        type: "textarea" },
      { key: "exploratory_objectives",label: "Exploratory objectives",      type: "textarea" },
      { key: "design_summary",        label: "Design summary *",           type: "textarea", required: true, placeholder: "e.g., open-label SAD then MAD; randomized 2:1 vs SoC" },
      { key: "population",            label: "Population *",                type: "textarea", required: true },
      { key: "inclusion_criteria",    label: "Inclusion criteria",         type: "textarea" },
      { key: "exclusion_criteria",    label: "Exclusion criteria",         type: "textarea" },
      { key: "dose_regimen",          label: "Dose regimen *",             type: "textarea", required: true },
      { key: "n_subjects",            label: "N subjects",                  type: "number" },
      { key: "duration_weeks",        label: "Duration (weeks)",            type: "number" },
      { key: "stats_summary",         label: "Statistical plan",            type: "textarea" },
      { key: "safety_monitoring",     label: "Safety monitoring (DSMB, stopping rules)", type: "textarea" },
      { key: "version",               label: "Protocol version",           type: "text" },
    ],
  },
  {
    code: "rems",
    label: "REMS",
    icon: HeartPulse,
    hint: "Risk Evaluation & Mitigation Strategy",
    path: "/therapeutics/rems/generate",
    docType: "rems",
    heading: "REMS Builder (21 U.S.C. §355-1).",
    subtitle:
      "Draft the REMS document FDA expects: SMART goals, ETASU elements, implementation system, and 18-month / 3-year / 7-year assessment timetable.",
    initial: {
      product_name: "", active_ingredient: "", sponsor: "", application_number: "",
      indication: "", serious_risk: "", goals: "",
      elements: ["medication_guide"],
      prescriber_cert_details: "", pharmacy_cert_details: "",
      patient_enrollment_details: "", monitoring_details: "",
      restricted_distribution: "", timetable_for_submission: "",
      version: "1.0",
    },
    fields: [
      { key: "product_name",       label: "Product *",                type: "text", required: true },
      { key: "active_ingredient",  label: "Active ingredient *",      type: "text", required: true },
      { key: "sponsor",            label: "Sponsor",                   type: "text" },
      { key: "application_number", label: "Application # (NDA/BLA)",   type: "text" },
      { key: "indication",         label: "Indication *",             type: "text", required: true },
      { key: "serious_risk",       label: "Serious risk driving REMS *", type: "textarea", required: true, placeholder: "e.g., embryofetal toxicity; severe hepatotoxicity" },
      { key: "goals",              label: "SMART REMS goals (optional; AI will draft if blank)", type: "textarea" },
      {
        key: "elements", label: "Selected REMS elements", type: "checkboxes",
        options: [
          { value: "medication_guide",       label: "Medication Guide" },
          { value: "communication_plan",     label: "Communication Plan (DHCP letter)" },
          { value: "prescriber_cert",        label: "Prescriber certification (ETASU)" },
          { value: "pharmacy_cert",          label: "Pharmacy / HCS certification (ETASU)" },
          { value: "patient_enrollment",     label: "Patient enrollment / registry (ETASU)" },
          { value: "monitoring",             label: "Monitoring (labs / imaging) (ETASU)" },
          { value: "restricted_distribution",label: "Restricted distribution" },
        ],
      },
      { key: "prescriber_cert_details",     label: "Prescriber certification details", type: "textarea" },
      { key: "pharmacy_cert_details",       label: "Pharmacy / HCS certification details", type: "textarea" },
      { key: "patient_enrollment_details",  label: "Patient enrollment / registry details", type: "textarea" },
      { key: "monitoring_details",          label: "Monitoring details (labs, imaging, adherence)", type: "textarea" },
      { key: "restricted_distribution",     label: "Restricted-distribution details", type: "textarea" },
      { key: "timetable_for_submission",    label: "Assessment timetable", type: "text", placeholder: "e.g., 18 months, 3 years, 7 years" },
      { key: "version",                     label: "REMS version", type: "text" },
    ],
  },
  {
    code: "ib",
    label: "IB Builder",
    icon: BookOpen,
    hint: "Investigator's Brochure",
    path: "/therapeutics/ib/generate",
    docType: "investigator_brochure",
    heading: "Investigator's Brochure (ICH E6 R3).",
    subtitle:
      "Every IND requires one. Full 7-section IB grounded in sponsor-provided nonclinical, CMC, clinical, and safety data.",
    initial: {
      program_name: "", active_ingredient: "", sponsor: "", indication: "",
      mechanism: "", nonclinical_summary: "", clinical_summary: "",
      cmc_summary: "", safety_signals: "", version: "1.0",
    },
    fields: [
      { key: "program_name",        label: "Program name *",           type: "text", required: true },
      { key: "active_ingredient",   label: "Active ingredient *",      type: "text", required: true },
      { key: "sponsor",             label: "Sponsor",                   type: "text" },
      { key: "indication",          label: "Indication *",             type: "text", required: true },
      { key: "mechanism",           label: "Mechanism of action",       type: "textarea" },
      { key: "nonclinical_summary", label: "Nonclinical summary (pharm/PK/tox)", type: "textarea" },
      { key: "clinical_summary",    label: "Clinical summary (if any)", type: "textarea" },
      { key: "cmc_summary",         label: "CMC summary",               type: "textarea" },
      { key: "safety_signals",      label: "Known safety signals",      type: "textarea" },
      { key: "version",             label: "IB version",                type: "text" },
    ],
  },
];

// -------- component -----------------------------------------------------------
export default function TherapeuticsDraftingPanel({ project, labelAnchor, onClearAnchor }) {
  const [mode, setMode] = useState("csr");
  const drafter = useMemo(() => DRAFTERS.find((d) => d.code === mode), [mode]);
  const [form, setForm] = useState(drafter.initial);
  const [content, setContent] = useState("");
  const [meta, setMeta] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);

  React.useEffect(() => {
    // Merge base initial with any inbound label anchor
    const base = { ...drafter.initial };
    if (labelAnchor) {
      const generic = labelAnchor.generic || "";
      const brand = labelAnchor.brand || "";
      if ("active_ingredient" in base) base.active_ingredient = generic || brand;
      if ("product_name" in base) base.product_name = brand || generic;
      if ("program_name" in base && !base.program_name) base.program_name = brand || generic;
      if ("sponsor" in base && labelAnchor.marketer) base.sponsor = labelAnchor.marketer;
      base.dailymed_setid = labelAnchor.setid;
    }
    setForm(base);
    setContent("");
    setMeta(null);
    setSessionId(null);
  }, [mode, labelAnchor]); // eslint-disable-line

  const setField = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const toggleCheckbox = (k, value) => {
    setForm((f) => {
      const cur = Array.isArray(f[k]) ? f[k] : [];
      return { ...f, [k]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] };
    });
  };

  const validate = () => {
    for (const field of drafter.fields) {
      if (field.required) {
        const v = form[field.key];
        if (v === undefined || v === null || String(v).trim() === "") {
          toast.error(`${field.label.replace(/\s*\*$/, "")} is required`);
          return false;
        }
      }
    }
    return true;
  };

  const run = async () => {
    if (!validate()) return;
    setStreaming(true);
    setContent("");
    setMeta(null);
    // Cast numeric fields
    const payload = { ...form };
    drafter.fields.forEach((f) => {
      if (f.type === "number") payload[f.key] = payload[f.key] === "" || payload[f.key] === null ? null : Number(payload[f.key]);
    });
    // Protocol: prepend the ASCII schema to design_summary so the AI grounds itself in it
    if (mode === "protocol") {
      const ascii = schemaAscii({
        duration_weeks: payload.duration_weeks,
        phase: payload.phase,
        treatment_arms: payload.treatment_arms,
        n_subjects: payload.n_subjects,
      });
      payload.design_summary = `${ascii}\n\n${payload.design_summary || ""}`.trim();
      logChuck("schema_rendered", {
        doc_type: drafter.docType,
        payload: { phase: payload.phase, duration_weeks: payload.duration_weeks, n_subjects: payload.n_subjects },
      });
    }
    let buf = "";
    let sess = null;
    try {
      await streamSSE(drafter.path, payload, (frame) => {
        if (frame.session_id) { sess = frame.session_id; setSessionId(frame.session_id); }
        if (frame.faers) setMeta((m) => ({ ...(m || {}), faers: frame.faers }));
        if (frame.delta) { buf += frame.delta; setContent(buf); }
        if (frame.error) toast.error(frame.error);
        if (frame.done) {
          // Client-side Chuck event too — some setups only have client telemetry
          logChuck("draft_generated", { doc_type: drafter.docType, session_id: sess,
            payload: { phase: form.phase, indication: form.indication, report_type: form.report_type } });
        }
      });
    } catch (e) {
      toast.error(e.message || "Draft failed");
    } finally {
      setStreaming(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(content);
    toast.success(`${drafter.label} copied`);
    logChuck("draft_copied", { doc_type: drafter.docType, session_id: sessionId,
      payload: { chars: content.length } });
  };

  const downloadMd = () => {
    const title = (form.study_title || form.protocol_title || form.product_name || form.program_name || drafter.label).slice(0, 60);
    const blob = new Blob([content], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/\s+/g, "_")}_${drafter.code}.md`;
    a.click();
    URL.revokeObjectURL(url);
    logChuck("draft_downloaded", { doc_type: drafter.docType, session_id: sessionId,
      payload: { format: "md", chars: content.length } });
  };

  const downloadDocx = async () => {
    const title = (form.study_title || form.protocol_title || form.product_name || form.program_name || drafter.label).slice(0, 60);
    try {
      await downloadExport(`${title}_${drafter.code}`, content, "docx");
      logChuck("draft_downloaded", { doc_type: drafter.docType, session_id: sessionId,
        payload: { format: "docx", chars: content.length } });
    } catch (e) {
      toast.error("DOCX export failed");
    }
  };

  return (
    <div data-testid="therapeutics-drafting-root" className="max-w-7xl">
      <div className="mb-6">
        <div className="overline mb-2 text-[#2563EB]">THERAPEUTICS DRAFTING · AI + LIVE REGULATORY DATA</div>
        <h1 className="font-serif text-3xl font-semibold tracking-tight lg:text-4xl">
          {drafter.heading}
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">{drafter.subtitle}</p>
        {labelAnchor && (
          <div data-testid="anchor-pill" className="mt-3 inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-[11px] text-emerald-800">
            <span className="overline">ANCHORED · DAILYMED</span>
            <span className="font-mono">{(labelAnchor.setid || "").slice(0, 8)}…</span>
            <span>· {labelAnchor.brand || labelAnchor.generic}</span>
            {onClearAnchor && (
              <button
                data-testid="anchor-clear"
                onClick={onClearAnchor}
                className="ml-2 border border-emerald-400 bg-white px-1.5 py-0.5 text-[10px] font-semibold text-emerald-800 hover:bg-emerald-100"
              >
                CLEAR
              </button>
            )}
          </div>
        )}
      </div>

      {/* Drafter tabs */}
      <div className="mb-6 grid gap-2 md:grid-cols-5">
        {DRAFTERS.map((d) => {
          const I = d.icon;
          const active = mode === d.code;
          return (
            <button
              key={d.code}
              data-testid={`ther-draft-mode-${d.code}`}
              onClick={() => setMode(d.code)}
              className={`flex items-center gap-3 border p-3 text-left transition-all ${
                active ? "border-[#2563EB] bg-[#2563EB]/5" : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <I className={`h-5 w-5 ${active ? "text-[#2563EB]" : "text-slate-500"}`} strokeWidth={1.5} />
              <div>
                <div className={`text-sm font-medium ${active ? "text-slate-900" : "text-slate-700"}`}>{d.label}</div>
                <div className="text-[11px] text-slate-500">{d.hint}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-12">
        {/* Form column */}
        <div className="lg:col-span-5">
          <div className="space-y-3 border border-slate-200 bg-white p-5">
            {drafter.fields.map((field) => (
              <Field key={field.key} field={field} value={form[field.key]}
                     onChange={setField(field.key)} onToggle={toggleCheckbox}
                     testid={`${drafter.code}-${field.key}`} />
            ))}
            {mode === "csr" && (
              <CsrTableImport
                docType={drafter.docType}
                testidBase="csr"
                onInject={(fieldKey, injected) =>
                  setForm((f) => ({
                    ...f,
                    [fieldKey]: (f[fieldKey] ? f[fieldKey] + "\n\n" : "") + injected,
                  }))
                }
              />
            )}
            <button
              data-testid={`${drafter.code}-run`}
              onClick={run}
              disabled={streaming}
              className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#2563EB] px-5 py-3 text-sm font-semibold text-white hover:bg-[#1D4ED8] disabled:opacity-40"
            >
              {streaming ? <Loader2 className="h-4 w-4 animate-spin" /> : <Beaker className="h-4 w-4" />}
              {content ? `Regenerate ${drafter.label}` : `Generate ${drafter.label}`}
            </button>
          </div>

          {drafter.metaRender && meta && (
            <div className="mt-4 text-xs">
              {drafter.metaRender(meta)}
            </div>
          )}

          {mode === "psur" && form.active_ingredient && form.reporting_period_start && form.reporting_period_end && (
            <div className="mt-4">
              <PsurSignalChart
                activeIngredient={form.active_ingredient}
                start={form.reporting_period_start}
                end={form.reporting_period_end}
                docType={drafter.docType}
              />
            </div>
          )}

          {mode === "protocol" && (
            <div className="mt-4">
              <ProtocolSchemaDiagram form={form} />
            </div>
          )}
        </div>

        {/* Output column */}
        <div className="lg:col-span-7">
          <div className="min-h-[420px] border border-slate-200 bg-white">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
              <div className="flex items-center gap-2">
                <drafter.icon className="h-4 w-4 text-[#2563EB]" />
                <span className="overline text-[#2563EB]">{drafter.label.toUpperCase()} DRAFT</span>
                {streaming && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              </div>
              {content && (
                <div className="flex items-center gap-2">
                  <button data-testid={`${drafter.code}-copy`} onClick={copy} className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"><Copy className="h-3 w-3" /> Copy</button>
                  <button data-testid={`${drafter.code}-download-md`} onClick={downloadMd} className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"><Download className="h-3 w-3" /> Markdown</button>
                  <button data-testid={`${drafter.code}-download-docx`} onClick={downloadDocx} className="inline-flex items-center gap-1.5 border border-slate-300 px-2 py-1 text-[11px] text-slate-700 hover:border-slate-400"><Download className="h-3 w-3" /> DOCX</button>
                </div>
              )}
            </div>
            <div data-testid={`${drafter.code}-output`} className="max-h-[75vh] overflow-y-auto p-6">
              {content ? (
                <Markdown>{content}</Markdown>
              ) : (
                <div className="flex h-96 flex-col items-center justify-center text-center text-slate-500">
                  <ShieldCheck className="mb-3 h-6 w-6 text-[#2563EB]" strokeWidth={1.5} />
                  <div className="text-sm">Fill the form on the left and hit <b>Generate {drafter.label}</b>.</div>
                  <div className="mt-1 text-xs text-slate-400">Chuck learns from your edits (only if you consented + not Enterprise).</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ field, value, onChange, onToggle, testid }) {
  if (field.type === "checkboxes") {
    return (
      <div>
        <div className="overline mb-2 text-slate-500">{field.label}</div>
        <div className="grid grid-cols-1 gap-1.5">
          {field.options.map((opt) => (
            <label key={opt.value} className="flex items-center gap-2 text-xs text-slate-700">
              <input
                data-testid={`${testid}-${opt.value}`}
                type="checkbox"
                checked={Array.isArray(value) && value.includes(opt.value)}
                onChange={() => onToggle(field.key, opt.value)}
                className="accent-[#2563EB]"
              />
              {opt.label}
            </label>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <label className="block">
        <div className="overline mb-1 text-slate-500">{field.label}</div>
        <select data-testid={testid} value={value ?? ""} onChange={onChange}
                className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900">
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className="block">
        <div className="overline mb-1 text-slate-500">{field.label}</div>
        <textarea data-testid={testid} value={value ?? ""} onChange={onChange} rows={3}
                  placeholder={field.placeholder || ""}
                  className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
      </label>
    );
  }
  return (
    <label className="block">
      <div className="overline mb-1 text-slate-500">{field.label}</div>
      <input data-testid={testid} type={field.type || "text"} value={value ?? ""} onChange={onChange}
             placeholder={field.placeholder || ""}
             className="w-full border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900" />
    </label>
  );
}
